import React, { useState, useEffect, useContext, useMemo } from 'react';
import useStaticGridData from '../hooks/useStaticGridData';
import { SimplifiedChampion } from '../../XXX/gameTypes';
import { ChampionConfig } from '../types/staticTypes';
import '../../XXX/GameSimulator/GameContainer2/GameGrid/GameGrid.css'; // Use exact same CSS
import StaticChampionCard from './StaticChampionCard';
import MatchupList from './MatchupList';
import './StaticGameGrid.css';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import GridEditor from './GridEditor';
import WaveAnalysisGrid, { WaveAnalysisResponse } from './WaveAnalysisGrid';
import { staticDataService } from '../services/staticDataService';
import { WaveExportRequest } from './GridEditor/WaveExport';
import { StaticGridEditor } from './StaticComponents';


interface StaticGameGridProps {
  championName: string; // Required - no sandbox mode
  defaultOpponent?: string;
  defaultLane?: string;
  onExportToDamageCalculator?: (mainChampion: ChampionConfig, targetChampion: ChampionConfig) => void;
  onOpponentChange?: (opponent: string) => void;
}

const StaticGameGrid: React.FC<StaticGameGridProps> = ({ championName, defaultOpponent, defaultLane, onExportToDamageCalculator, onOpponentChange }) => {
  // State to track the current opponent name across all rows
  const [currentGlobalOpponent, setCurrentGlobalOpponent] = useState<string>(defaultOpponent || 'Garen');
  
  // Removed level sections state - now using unified grid
  
  const { gridData, loading, error, loadingNextBatch, refetch, fetchCurrentGrid } = useStaticGridData(championName, currentGlobalOpponent);
  
  // State to manage champions that can be updated
  const [champions, setChampions] = useState<SimplifiedChampion[][]>([]);

  const { gameWideAssets } = useContext(GameAssetsContext);
  const openInCalculatorImage = gameWideAssets.damageIcon;

    // 🌊 NEW: wave export local state
    const [waveResp, setWaveResp] = useState<WaveAnalysisResponse | null>(null);
    const [waveLoading, setWaveLoading] = useState(false);
    const [waveError, setWaveError] = useState<string | null>(null);
    
    // Track which waves are currently in the preview/grid for styling in export panel
    const currentPreviewWaves = useMemo(() => {
      const waves = waveResp?.data?.metadata?.waves || [];
      console.log('[StaticGameGrid] 🔍 currentPreviewWaves calculation:', {
        hasWaveResp: !!waveResp,
        hasMetadata: !!waveResp?.data?.metadata,
        metadataWaves: waveResp?.data?.metadata?.waves,
        resultingWaves: waves,
        waveCount: waves.length
      });
      return waves;
    }, [waveResp]);
  
  // Sync opponent state when defaultOpponent prop arrives from API
  useEffect(() => {
    if (defaultOpponent) {
      setCurrentGlobalOpponent(defaultOpponent);
    }
  }, [defaultOpponent]);

  // Update local champions state when gridData changes
  useEffect(() => {
    if (gridData?.champions) {
      setChampions(gridData.champions);
    }
  }, [gridData]);

  // Clear wave analysis state when champion changes
  useEffect(() => {
    console.log(`[StaticGameGrid] 🔄 Champion changed to: ${championName}, clearing wave analysis state`);
    setWaveResp(null);
    setWaveError(null);
    setWaveLoading(false);
  }, [championName]);

  // Handle champion updates from StaticChampionCard
  const handleChampionUpdate = (updatedChampion: SimplifiedChampion) => {
    console.log('Updating champion in grid:', updatedChampion.name, updatedChampion.id);
    
    setChampions(prevChampions => {
      return prevChampions.map(row => 
        row.map(champion => {
          // Match by unique ID to ensure we update the correct champion
          if (champion && champion.id === updatedChampion.id) {
            console.log('Found and updating champion by ID:', champion.id, champion.name, champion.level);
            return updatedChampion;
          }
          return champion;
        })
      );
    });
  };

  // Handle opponent changes from MatchupList
  const handleOpponentChange = (newOpponent: string) => {
    console.log('Opponent changed to:', newOpponent);
    
    // Clear wave analysis state when opponent changes to prevent showing old data with errors
    console.log(`[StaticGameGrid] 🔄 Opponent changed to: ${newOpponent}, clearing wave analysis state`);
    setWaveResp(null);
    setWaveError(null);
    setWaveLoading(false);
    
    // Update the global opponent state (this will trigger useEffect in useStaticGridData)
    setCurrentGlobalOpponent(newOpponent);
    
    // Notify parent component about the opponent change
    if (onOpponentChange) {
      onOpponentChange(newOpponent);
    }
  };

  // Toggle section collapse state
  // Removed level grouping functions - now using unified grid

  // Update global opponent state when grid data changes
  useEffect(() => {
    if (gridData?.champions && gridData.champions.length > 0) {
      // Get the opponent from the first row to set as global opponent
      const firstRowOpponent = gridData.champions[0] && gridData.champions[0][1];
      if (firstRowOpponent) {
        setCurrentGlobalOpponent(firstRowOpponent.name);
      }
    }
  }, [gridData]);

  // Export to Damage Calculator functionality
  const handleExportToDamageCalculator = (rowIndex: number) => {
    const row = champions[rowIndex];
    if (!row || row.length < 2) {
      console.error('Invalid row for export:', row);
      return;
    }

    const mainChampion = row[0];
    const targetChampion = row[1];

    if (!mainChampion || !targetChampion) {
      console.error('Missing champions in row:', { mainChampion, targetChampion });
      return;
    }

    // Extract champion configurations
    const extractChampionConfig = (champion: SimplifiedChampion): ChampionConfig => {
      return {
        name: champion.name,
        level: champion.level,
        team: champion.team,
        items: champion.inventory?.items?.map(item => item.id) || [],
        runes: champion.runePage ? {
          primaryPath: champion.runePage.primaryPath,
          primaryRunes: champion.runePage.primaryRunes,
          secondaryPath: champion.runePage.secondaryPath,
          secondaryRunes: champion.runePage.secondaryRunes,
          shards: champion.runePage.shards
        } : null,
        skillOrder: champion.currentSkillOrder || []
      };
    };

    const mainConfig = extractChampionConfig(mainChampion);
    const targetConfig = extractChampionConfig(targetChampion);

    console.log('Exporting champions to damage calculator:', { mainConfig, targetConfig });

    // Use prop callback if provided, otherwise fallback to URL method
    if (onExportToDamageCalculator) {
      onExportToDamageCalculator(mainConfig, targetConfig);
    } else {
      // Fallback to URL method
      const params = new URLSearchParams();
      params.set('mainChampion', JSON.stringify(mainConfig));
      params.set('targetChampion', JSON.stringify(targetConfig));

      const damageCalculatorUrl = `/damage-calculator?${params.toString()}`;
      
      // Open in new tab
      window.open(damageCalculatorUrl, '_blank');
    }
  };

  // Add row functionality - adds a row after the specified rowIndex
  const handleAddRow = async (rowIndex: number) => {
    console.log(`Requesting to add row after index ${rowIndex}, current grid size: ${champions.length}x${champions[0]?.length}`);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/grid/add-row`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championName,
          position: rowIndex,
          direction: 'below'
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success && result.data) {
        console.log('Successfully added row, updating grid');
        setChampions(result.data.champions);
      } else {
        console.error('Failed to add row:', result.error);
      }
    } catch (error) {
      console.error('Error adding row:', error);
    }
  };
  
  // Duplicate row functionality - copies the champions from the specified row to a new row below
  const handleDuplicateRow = async (rowIndex: number) => {
    console.log(`Requesting to duplicate row at index ${rowIndex}, current grid size: ${champions.length}x${champions[0]?.length}`);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static/grid/duplicate-row`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          championName: championName,
          position: rowIndex
        }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      if (result.success && result.data) {
        console.log('Successfully duplicated row, updating grid');
        setChampions(result.data.champions);
      } else {
        console.error('Failed to duplicate row:', result.error);
      }
    } catch (error) {
      console.error('Error duplicating row:', error);
    }
  };

  // Render row separators with add button
  const renderRowSeparator = (rowIndex: number) => (
    <div className="row-separator" key={`separator-${rowIndex}`}>
      <div className="row-add-buttons">
        <button 
          className="row-add-button" 
          onClick={() => handleAddRow(rowIndex)}
          title="Add empty row here"
        >
          +
        </button>
        <button 
          className="row-add-button duplicate-button" 
          onClick={() => handleDuplicateRow(rowIndex)}
          title="Duplicate this row"
        >
          ⎘
        </button>
        <button 
          className="row-add-button export-button" 
          onClick={() => handleExportToDamageCalculator(rowIndex)}
          title="Export to Damage Calculator"
        >
          <img src={openInCalculatorImage} alt="Open in Calculator" />
                </button>
      </div>
    </div>
  );

   // 🔌 NEW: handle wave export -> POST to backend and just display response
  // GridEditor will call this with your “wave export” payload
  const handleWaveExport = async (payload: WaveExportRequest) => {
    try {
      setWaveError(null);
      setWaveLoading(true);
      const resp = await staticDataService.exportWaveGrid(payload);
      setWaveResp(resp);

      // Optional: also refresh the current grid, if you want the main grid to reflect it.
      // await fetchCurrentGrid();

    } catch (e: any) {
      setWaveError(e?.message || 'Wave export failed');
      setWaveResp(null);
    } finally {
      setWaveLoading(false);
      // Optionally scroll into view like you already do:
      setTimeout(() => {
        const el = document.querySelector('.wave-export-preview') || document.querySelector('.game-grid');
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };


  if (loading) {
    return (
      <div className="static-loading-container">
        <div className="simple-spinner"></div>
        <p>Loading {championName} data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="static-game-grid-error">
        <div className="error-message">
          <h3>Error loading grid data</h3>
          <p>{error}</p>
          <button onClick={() => refetch()} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!gridData) {
    return (
      <div className="static-game-grid-empty">
        <p>No grid data available</p>
      </div>
    );
  }

  const { metadata } = gridData;
  const batchInfo = metadata.batchInfo;

  // Get wave metadata for displaying wave information
  const getWaveInfo = (rowIndex: number) => {
    const rowMeta = gridData?.metadata?.rows?.[rowIndex];
    if (rowMeta && rowMeta.kind === 'wave') {
      return {
        waveNumber: rowMeta.waveNumber,
        arrivalTime: rowMeta.arrivalTime,
        clearTime: rowMeta.clearTime
      };
    }
    return null;
  };

  const handleGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  return (
      <div className='game-grid' onClick={handleGridClick}>
        {/* Matchup List Section with integrated opponent selector */}
        <MatchupList 
          championName={championName}
          currentOpponent={currentGlobalOpponent}
          onOpponentSelect={handleOpponentChange}
        />
        
        {/* Static Grid Editor - Backend Optimized */}
        <StaticGridEditor 
          championName={championName}
          opponentName={currentGlobalOpponent}
          defaultLane={defaultLane}
          loadedWaves={currentPreviewWaves}
          analysisGridResponse={waveResp} // Pass the full analysis grid response
          onBuildOrderChange={async (championName, buildOrder) => {
            console.log(`[StaticGameGrid] Build order changed for ${championName}:`, buildOrder);
            // TODO: Call backend API to update champion build order and recalculate waves
          }}
          onWaveExport={async (selectedWaves, championName) => {
            console.log(`[StaticGameGrid] Wave export requested for ${championName}, waves:`, selectedWaves);
            // TODO: Call backend API for simplified wave export
          }}
          onWaveExportResult={(result, error) => {
            console.log(`[StaticGameGrid] 📨 Wave export result received:`, {
              hasResult: !!result,
              hasError: !!error,
              resultSuccess: result?.success,
              resultHasData: !!result?.data,
              resultHasChampions: !!result?.data?.champions,
              championCount: result?.data?.champions?.length || 0,
              error: error
            });
            
            if (error) {
              setWaveError(error);
              setWaveResp(null);
            } else if (result) {
              setWaveError(null);
              setWaveResp(result);
              console.log(`[StaticGameGrid] ✅ Set wave response with ${result?.data?.champions?.length || 0} champion rows`);
            }
          }}
          onGoldEventsChange={async (championName, goldEvents) => {
            console.log(`[StaticGameGrid] Gold events changed for ${championName}:`, goldEvents);
            // TODO: Call backend API to update gold events
          }}
          className="static-grid-editor"
        />
        
     

        <WaveAnalysisGrid 
          response={waveResp} 
          loading={waveLoading} 
          error={waveError}
          onExportToDamageCalculator={onExportToDamageCalculator}
          onAddRow={handleAddRow}
          onDuplicateRow={handleDuplicateRow}
          championName={championName}
          defaultLane={defaultLane}
          enablePolling={true} // Enable polling to check for new champions
          onPollingUpdate={(updatedResponse) => {
            console.log('[StaticGameGrid] Received polling update:', updatedResponse);
            setWaveResp(updatedResponse);
          }}
        />
        
       

      </div>
  );
};

export default StaticGameGrid; 